import {
  add,
  endOfWeek,
  startOfDay,
  startOfToday,
  startOfWeek,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import {
  EVENT_ECONOMIC_COUNTRY_FILTER,
  useRemoteStorage,
} from '~/hooks/use-remote-storage';
import { useEarningsFilterStats } from '~/react-query-hooks/earnings/useEarningsFilterStats';
import { KEY_DEV_TYPES_EXCEPT_EARNINGS } from '~/react-query-hooks/events/useCompanyEvents';
import { useFinancialEventsCalendar } from '~/react-query-hooks/events/useFinancialEventsCalendar';
import { queryKeys } from '~/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';

import * as S from './CalendarView.styles';
import { EventOverview } from './event-overview/EventOverview';
import { EventTabs } from './event-tabs/EventTabs';
import {
  CountryFilter,
  formatEarningsCount,
  formatEconomicsCompanyCount,
  formatEventDate,
  isDateBeyondThreeMonths,
  TabsType,
} from './utils';

export const CalendarView = () => {
  const { t } = useTranslation(['calendar']);

  const { items } = useRemoteStorage(state => ({
    items: state.items,
  }));

  const DEFAULT_COUNTRY_FILTER: CountryFilter = {
    key: 'US',
    name: 'United States',
    value: { value: 'US' },
  };

  const storedFilter = items?.[EVENT_ECONOMIC_COUNTRY_FILTER];
  const defaultCountryFilter: CountryFilter[] = Array.isArray(storedFilter)
    ? (storedFilter as CountryFilter[])
    : [DEFAULT_COUNTRY_FILTER];

  const today = startOfToday();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeDate, setActiveDate] = useState(today);
  const [earningsOnlyWatchlists, setEarningsOnlyWatchlists] = useState(false);
  const [companyOnlyWatchlists, setCompanyOnlyWatchlists] = useState(false);
  const [activeCountries, setActiveCountries] = useState(defaultCountryFilter);
  const [forceIsTableLoading, setForceIsTableLoading] = useState(true);

  const { params } = useQueryParams();

  const dateThreeMonthsLater = add(today, { months: 3 });
  const maxDate = startOfDay(dateThreeMonthsLater);

  useEffect(() => {
    const activeDateSearchParam = searchParams.get(
      queryKeys.activeCalendarDate
    );

    if (activeDateSearchParam) {
      const parsedDate = new Date(parseFloat(activeDateSearchParam));

      setActiveDate(parsedDate);
    }
  }, [searchParams]);

  const monday = startOfWeek(activeDate, { weekStartsOn: 1 });
  const sunday = endOfWeek(activeDate, { weekStartsOn: 1 });
  const isNextWeekDisabled = isDateBeyondThreeMonths(sunday);
  const endDate = isNextWeekDisabled ? maxDate : sunday;

  const activeCountriesValues = activeCountries.map(
    country => country.value.value
  );

  const { data: earningsCounts, isLoading: earningsCountsLoading } =
    useEarningsFilterStats({
      start_date: monday.toISOString(),
      end_date: endDate.toISOString(),
      watchlists: {
        all: earningsOnlyWatchlists,
      },
    });
  const {
    data: economicsAndCompanyCounts,
    isLoading: economicsCompanyCountsLoading,
  } = useFinancialEventsCalendar({
    from: monday.toISOString(),
    to: endDate.toISOString(),
    countries: activeCountriesValues.length ? activeCountriesValues : undefined,
    types: KEY_DEV_TYPES_EXCEPT_EARNINGS,
    watchlists: {
      all: companyOnlyWatchlists,
    },
  });
  const { economics: economicsCounts, companies: companiesCounts } =
    formatEconomicsCompanyCount(economicsAndCompanyCounts);

  const countData = {
    [TabsType.Economics]: {
      counts: economicsCounts,
      isLoading: economicsCompanyCountsLoading,
    },
    [TabsType.Earnings]: {
      counts: formatEarningsCount(earningsCounts),
      isLoading: earningsCountsLoading,
    },
    [TabsType.Company]: {
      counts: companiesCounts,
      isLoading: economicsCompanyCountsLoading,
    },
  };

  const handleToggle = (checked: boolean, type: TabsType) => {
    if (type === TabsType.Earnings) {
      setEarningsOnlyWatchlists(checked);
    } else {
      setCompanyOnlyWatchlists(checked);
    }
  };

  const onDateChange = (date: Date) => {
    const activeDateSearchParam = searchParams.get(
      queryKeys.activeCalendarDate
    );

    if (activeDateSearchParam) {
      setSearchParams(params => {
        params.delete(queryKeys.activeCalendarDate);
        return params;
      });
    }

    setForceIsTableLoading(true);
    setActiveDate(date);
  };

  const activeTab =
    (searchParams.get(queryKeys.activeCalendarTab) as TabsType) ||
    TabsType.Earnings;

  const hasEvents = !!countData[activeTab].counts[formatEventDate(activeDate)];

  return (
    <WithFooterWrapper
      emptyState={!hasEvents}
      questionContext={`${activeDate}${activeTab}`}
      shouldHide={!!params[queryKeys.activeDrawer]}
      trackingMetadata={{ source: POSTHOG_TRACKING_SOURCES.CALENDAR }}
    >
      <S.Container data-testid="calendar-view">
        <S.EventsWrapper>
          <S.Title>{t('calendar:pageTitle')}</S.Title>
          <EventOverview
            activeDate={activeDate}
            startWeekDay={monday}
            endWeekDay={endDate}
            isNextWeekDisable={isNextWeekDisabled}
            data={countData}
            onDateChange={onDateChange}
          />
        </S.EventsWrapper>
        <S.TableContentWrapper>
          <EventTabs
            activeDate={activeDate}
            earningsOnlyWatchlists={earningsOnlyWatchlists}
            companyOnlyWatchlists={companyOnlyWatchlists}
            activeCountries={activeCountries}
            forceIsTableLoading={forceIsTableLoading}
            setForceIsTableLoading={setForceIsTableLoading}
            setActiveCountries={setActiveCountries}
            handleToggle={handleToggle}
            onDateChange={onDateChange}
          />
        </S.TableContentWrapper>
      </S.Container>
    </WithFooterWrapper>
  );
};
